import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GitCompare, GitBranch, Pencil, Plus, Repeat2, Star } from 'lucide-react';
import {
  COCKTAIL_STATUSES,
  amountsRatio,
  canEditInPlace,
  convertAmount,
  isVolumeUnit,
  partsRatio,
  type CocktailStatus,
} from '@muddle/core';
import { api } from '../api';
import { useAsync } from '../hooks/useAsync';
import { useTitle } from '../shortcuts';
import {
  ConfirmDialog,
  ErrorMessage,
  PageHeader,
  Skeleton,
  StatusPill,
  useToast,
} from '../components';
import { BalanceBars, TastingDialog } from '../TastingDialog';
import { estimateText, formatDate, lineText } from '../format';

type RatioMode = 'off' | 'amounts' | 'parts';

export function DrinkPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, reload] = useAsync(() => api.getCocktail(id), [id]);
  const [tastings, reloadTastings] = useAsync(() => api.listTastings(id), [id]);
  const [settings] = useAsync(api.getSettings);
  const [picked, setPicked] = useState<number | null>(null);
  const [unitOverride, setUnitOverride] = useState<'oz' | 'ml' | null>(null);
  const [ratio, setRatio] = useState<RatioMode>('off');
  useTitle(state.status === 'ready' ? state.data.cocktail.name : 'Drink');
  const [confirm, setConfirm] = useState<'archive' | 'delete' | null>(null);
  const [tasting, setTasting] = useState(false);

  if (state.status === 'loading') {
    return (
      <div className="page">
        <Skeleton height={160} />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="page">
        <ErrorMessage message={`Couldn't load this drink. ${state.message}`} onRetry={reload} />
        <p style={{ marginTop: 'var(--space-4)' }}>
          <Link to="/">Back to your drinks</Link>
        </p>
      </div>
    );
  }

  const { cocktail, parent, versions } = state.data;
  const current = versions[0];
  const selected = versions.find((v) => v.versionNumber === picked) ?? current;
  const unit = unitOverride ?? (settings.status === 'ready' ? settings.data.defaultUnit : 'oz');
  const archived = cocktail.archivedAt !== null;
  const viewingOld = selected !== undefined && current !== undefined && selected.id !== current.id;

  // Ratio uses measured volumes only (oz/ml); dashes, drops and pieces are left out.
  const volumeLines = selected?.lines.filter((l) => isVolumeUnit(l.unit)) ?? [];
  const ratioText =
    ratio === 'off' || volumeLines.length < 2
      ? ''
      : ratio === 'amounts'
        ? amountsRatio(volumeLines.map((l) => convertAmount(l.amount, l.unit, unit)))
        : partsRatio(volumeLines.map((l) => convertAmount(l.amount, l.unit, 'ml')));

  // Compare the selected version with the one before it (or the newest with the one before).
  const compareB = selected?.versionNumber;
  const compareA = selected
    ? versions.find((v) => v.versionNumber < selected.versionNumber)?.versionNumber
    : undefined;

  const run = async (fn: () => Promise<unknown>, ok: string, after?: () => void) => {
    try {
      await fn();
      toast(ok);
      after?.();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Something went wrong.', { error: true });
    }
  };

  return (
    <div className="page">
      <PageHeader
        kicker={archived ? 'Archived' : 'Drink'}
        title={cocktail.name}
        sub={
          parent ? (
            <>
              Riff on <Link to={`/drinks/${parent.id}`}>{parent.name}</Link>
            </>
          ) : undefined
        }
        actions={
          !archived && (
            <>
              <button className="btn btn-ghost" type="button" onClick={() => setTasting(true)}>
                <Plus size={16} aria-hidden /> Log tasting
              </button>
              <Link
                className="btn btn-primary"
                to={
                  viewingOld
                    ? `/drinks/${id}/edit?from=${selected.versionNumber}`
                    : `/drinks/${id}/edit`
                }
              >
                <Repeat2 size={16} aria-hidden /> Log tweak
              </Link>
            </>
          )
        }
      />

      {archived && (
        <div className="msg warn" role="status">
          This drink is archived and hidden from your library.{' '}
          <button
            className="btn btn-sm"
            type="button"
            onClick={() => void run(() => api.restoreCocktail(id), 'Restored', reload)}
          >
            Restore
          </button>
        </div>
      )}

      <section className="card" aria-labelledby="spec-title">
        <div className="mu-card-head">
          <h2 className="card-title" id="spec-title">
            {selected
              ? `Version ${selected.versionNumber}${viewingOld ? '' : ' (current)'}`
              : 'Spec'}
          </h2>
          <div className="tabs" role="group" aria-label="Units">
            {(['oz', 'ml'] as const).map((u) => (
              <button
                key={u}
                type="button"
                className="tab"
                aria-pressed={unit === u}
                onClick={() => setUnitOverride(u)}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        {selected && (
          <>
            <ul className="mu-spec">
              {selected.lines.map((l) => (
                <li key={l.id} className="mu-spec-line">
                  <span className="num mu-spec-amt">{lineText(l, unit)}</span>
                  <span>{l.ingredientName}</span>
                </li>
              ))}
            </ul>

            <div className="mu-estimate">
              <span className="mu-estimate-label">Estimate</span>
              <span className="num">{estimateText(selected.abvEst, selected.volumeEst, unit)}</span>
              <span className="muted"> · after dilution, not lab accurate</span>
            </div>

            <div className="chip-row" role="group" aria-label="Ratio display">
              {(
                [
                  ['off', 'No ratio'],
                  ['amounts', 'Ratio'],
                  ['parts', 'Parts'],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  className="chip"
                  aria-pressed={ratio === mode}
                  onClick={() => setRatio(mode)}
                >
                  {label}
                </button>
              ))}
            </div>
            {ratio !== 'off' && (
              <p className="mu-ratio num" aria-live="polite">
                {ratioText || 'Needs at least two oz or ml ingredients.'}
              </p>
            )}

            <dl className="mu-facts">
              <div>
                <dt>Technique</dt>
                <dd>{selected.technique}</dd>
              </div>
              {selected.glass && (
                <div>
                  <dt>Glass</dt>
                  <dd>{selected.glass}</dd>
                </div>
              )}
              {selected.ice && (
                <div>
                  <dt>Ice</dt>
                  <dd>{selected.ice}</dd>
                </div>
              )}
              {selected.garnish && (
                <div>
                  <dt>Garnish</dt>
                  <dd>{selected.garnish}</dd>
                </div>
              )}
            </dl>
            {selected.methodNotes && <p className="mu-notes">{selected.methodNotes}</p>}
            {!archived && (
              <div className="actions">
                {canEditInPlace(selected.tastingCount) && (
                  <Link
                    className="btn btn-ghost btn-sm"
                    to={`/drinks/${id}/edit?inplace=${selected.id}`}
                  >
                    <Pencil size={14} aria-hidden /> Edit in place
                  </Link>
                )}
                {viewingOld && (
                  <button
                    className="btn btn-plain btn-sm"
                    type="button"
                    onClick={() => setPicked(null)}
                  >
                    Back to current
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <section className="card" aria-labelledby="tast-title">
        <div className="mu-card-head">
          <h2 className="card-title" id="tast-title">
            Tastings
          </h2>
        </div>
        {tastings.status === 'loading' && <Skeleton height={48} />}
        {tastings.status === 'error' && (
          <ErrorMessage
            message={`Couldn't load tastings. ${tastings.message}`}
            onRetry={reloadTastings}
          />
        )}
        {tastings.status === 'ready' && tastings.data.length === 0 && (
          <div className="empty">
            <div className="empty-title">No tastings yet</div>
            Rate a version and note how the balance came out.
            {!archived && (
              <div>
                <button className="btn btn-primary" type="button" onClick={() => setTasting(true)}>
                  Log tasting
                </button>
              </div>
            )}
          </div>
        )}
        {tastings.status === 'ready' && tastings.data.length > 0 && (
          <ul className="mu-tastings">
            {tastings.data.map((t) => (
              <li key={t.id} className="mu-tasting">
                <div className="mu-tasting-head">
                  <span className="mu-rating" aria-label={`Rating ${t.rating} out of 5`}>
                    <Star size={14} aria-hidden /> <span className="num">{t.rating}/5</span>
                  </span>
                  <span className="muted">
                    v{t.versionNumber} · {formatDate(t.date)}
                  </span>
                  <button
                    className="btn btn-plain btn-sm"
                    type="button"
                    style={{ marginLeft: 'auto' }}
                    onClick={() =>
                      void run(
                        () => api.archiveTasting(t.id),
                        'Tasting removed',
                        () => {
                          reloadTastings();
                          reload();
                        },
                      )
                    }
                  >
                    Remove
                  </button>
                </div>
                <BalanceBars sweet={t.sweet} sour={t.sour} bitter={t.bitter} strong={t.strong} />
                {t.notes && <p className="mu-notes">{t.notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card" aria-labelledby="hist-title">
        <div className="mu-card-head">
          <h2 className="card-title" id="hist-title">
            Version history
          </h2>
          {compareA !== undefined && (
            <Link
              className="btn btn-ghost btn-sm"
              to={`/drinks/${id}/compare?a=${compareA}&b=${compareB}`}
            >
              <GitCompare size={14} aria-hidden /> Compare v{compareA} and v{compareB}
            </Link>
          )}
        </div>
        <ol className="mu-timeline">
          {versions.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                className="mu-tl-item"
                aria-pressed={selected?.id === v.id}
                onClick={() => setPicked(v.versionNumber)}
              >
                <span className="mu-tl-num num">v{v.versionNumber}</span>
                <span className="grow">
                  <span className="row-title">
                    {v.changeNote || (v.versionNumber === 1 ? 'First version' : 'No note')}
                  </span>
                  <span className="row-meta">
                    {formatDate(v.createdAt)}
                    {v.tastingCount > 0
                      ? ` · ${v.tastingCount} tasting${v.tastingCount === 1 ? '' : 's'}`
                      : ''}
                  </span>
                </span>
                {v.id === current?.id && <span className="pill">current</span>}
              </button>
            </li>
          ))}
        </ol>
      </section>

      <section className="card" aria-labelledby="about-title">
        <h2 className="card-title" id="about-title">
          About
        </h2>
        <div className="list-row">
          <label htmlFor="status">Status</label>
          <span className="spacer" style={{ marginLeft: 'auto' }}>
            {archived ? (
              <StatusPill status={cocktail.status} />
            ) : (
              <select
                id="status"
                value={cocktail.status}
                onChange={(e) =>
                  void run(
                    () => api.updateCocktail(id, { status: e.target.value as CocktailStatus }),
                    'Status updated',
                    reload,
                  )
                }
              >
                {COCKTAIL_STATUSES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            )}
          </span>
        </div>
        <div className="list-row">
          <span>Tags</span>
          <span style={{ marginLeft: 'auto' }}>
            {cocktail.tags.length ? cocktail.tags.map((t) => `#${t}`).join(' ') : 'None'}
          </span>
        </div>
        <div className="actions">
          {!archived && (
            <Link className="btn btn-ghost btn-sm" to={`/drinks/new?riff=${id}`}>
              <GitBranch size={14} aria-hidden /> Make a riff
            </Link>
          )}
          <span className="spacer" />
          {archived ? (
            <button
              className="btn btn-danger btn-sm"
              type="button"
              onClick={() => setConfirm('delete')}
            >
              Delete permanently
            </button>
          ) : (
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setConfirm('archive')}
            >
              Archive
            </button>
          )}
        </div>
      </section>

      <TastingDialog
        open={tasting}
        versions={versions.map((v) => ({ id: v.id, versionNumber: v.versionNumber }))}
        defaultVersionId={selected?.id ?? ''}
        onClose={() => setTasting(false)}
        onSaved={() => {
          setTasting(false);
          toast('Tasting saved');
          reloadTastings();
          reload();
        }}
      />
      <ConfirmDialog
        open={confirm === 'archive'}
        title={`Archive ${cocktail.name}?`}
        body="It will be hidden from your library. You can restore it any time."
        confirmLabel="Archive drink"
        danger={false}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void run(
            () => api.archiveCocktail(id),
            'Archived',
            () => navigate('/'),
          );
        }}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        title={`Delete ${cocktail.name} permanently?`}
        body={`This removes the drink with all ${versions.length} version${versions.length === 1 ? '' : 's'} and tastings. It can't be undone.`}
        confirmLabel="Delete permanently"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          void run(
            () => api.deleteCocktail(id),
            'Deleted',
            () => navigate('/'),
          );
        }}
      />
    </div>
  );
}
